import React from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, Button, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { usePlannerTheme } from '../../theme/plannerTheme';

const SEVERITY = {
  warning: { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', Icon: WarningIcon },
  error:   { color: '#ef4444', bg: '#fef2f2', border: '#fecaca', Icon: ErrorIcon },
  info:    { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', Icon: InfoIcon },
  success: { color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0', Icon: CheckCircleIcon },
};

/**
 * Reusable confirmation/alert dialog
 * @param {boolean} open
 * @param {function} onClose
 * @param {function} onConfirm - if null, dialog is just an alert (no confirm button)
 * @param {string} title
 * @param {string} message
 * @param {string} severity - 'warning' | 'error' | 'info' | 'success'
 * @param {string} confirmLabel
 * @param {string} cancelLabel
 */
function ConfirmDialog({ open, onClose, onConfirm, title, message, severity = 'warning', confirmLabel, cancelLabel }) {
  // Hook SEMPRE antes de qualquer return (regras dos hooks).
  const dark = usePlannerTheme() === 'dark';
  if (!open) return null;
  const s = SEVERITY[severity] || SEVERITY.warning;
  const SIcon = s.Icon;
  const isAlert = !onConfirm; // alert mode = no confirm, just close

  // Paleta por tema. Claro = cartão branco (como antes); Escuro = cartão navy,
  // a espelhar os tokens --surf-*/usePlannerDialogTheme. As cores de severidade
  // (header colorido + botão confirmar) mantêm-se vivas nos dois temas; no
  // escuro o fundo do header passa a uma versão translúcida da cor.
  const pal = dark
    ? {
        card: '#122133', cardBorder: '1px solid rgba(110,193,228,0.14)',
        shadow: '0 20px 60px rgba(0,0,0,0.55)',
        title: '#eaf3fb', msg: '#9fb3c8', sub: '#9fb3c8',
        headBg: `${s.color}22`, headBorder: `${s.color}55`,
        footBg: 'rgba(255,255,255,0.03)', footBorder: 'rgba(110,193,228,0.12)',
      }
    : {
        card: '#ffffff', cardBorder: 'none',
        shadow: '0 20px 60px rgba(0,0,0,0.2)',
        title: '#0f172a', msg: '#475569', sub: '#64748b',
        headBg: s.bg, headBorder: s.border,
        footBg: '#fafbfc', footBorder: '#f1f5f9',
      };

  // Portal para o <body> — sem isto, o z-index (9999) fica preso no contexto
  // de empilhamento do componente pai (ex.: dentro do planner/diálogos MUI
  // que portam para o body) e o ConfirmDialog aparecia POR DETRÁS desses
  // diálogos. No body, o overlay fica sempre no topo.
  return ReactDOM.createPortal(
    <>
      <Box onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(2px)',
      }} />
      <Box style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 9999, width: 380, maxWidth: '90vw',
        background: pal.card, border: pal.cardBorder, borderRadius: 16,
        boxShadow: pal.shadow,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px',
          background: pal.headBg, borderBottom: `1px solid ${pal.headBorder}`,
        }}>
          <Box style={{
            width: 36, height: 36, borderRadius: 10,
            background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 2px 8px ${s.color}40`,
          }}>
            <SIcon style={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Typography style={{ fontSize: '0.92rem', fontWeight: 700, color: pal.title, flex: 1 }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={onClose} style={{ color: pal.sub }}>
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box style={{ padding: '20px 24px' }}>
          <Typography style={{ fontSize: '0.8rem', color: pal.msg, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
            {message}
          </Typography>
        </Box>

        {/* Actions */}
        <Box style={{
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          padding: '12px 20px', borderTop: `1px solid ${pal.footBorder}`, background: pal.footBg,
        }}>
          <Button
            onClick={onClose}
            style={{
              textTransform: 'none', fontSize: '0.78rem', fontWeight: 600,
              color: pal.sub, borderRadius: 8, padding: '6px 16px',
            }}
          >
            {cancelLabel || (isAlert ? 'OK' : 'Cancelar')}
          </Button>
          {!isAlert && (
            <Button
              onClick={() => { onConfirm(); onClose(); }}
              style={{
                textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
                background: s.color, color: '#fff', borderRadius: 8, padding: '6px 20px',
                boxShadow: `0 2px 8px ${s.color}30`,
              }}
            >
              {confirmLabel || 'Confirmar'}
            </Button>
          )}
        </Box>
      </Box>
    </>,
    document.body
  );
}

export default ConfirmDialog;
